/**
 * CacheManager - TanStack Query-inspired cache management
 *
 * Provides unified cache invalidation and optimistic update patterns.
 */

import type { EventEmitter } from '../base/event-emitter.js';
import type { HandlerChain } from '../handlers/handler-chain.js';

/**
 * Options for cache invalidation (like TanStack's invalidateQueries)
 */
export interface InvalidateOptions {
  /** Invalidate by namespace */
  namespace?: string;
  /** Invalidate by locale */
  locale?: string;
  /** Invalidate specific key */
  key?: string;
  /** Custom predicate filter */
  predicate?: (key: string) => boolean;
  /** Whether to refetch after invalidation (default: true) */
  refetch?: boolean;
}

/**
 * Cached translation entry
 */
export interface CacheEntry {
  value: string;
  styles?: Record<string, string[]>;
}

/**
 * Cache event payloads
 */
export interface CacheEventPayloads {
  'cache:invalidate': {
    filter: InvalidateOptions;
    keysCleared: number;
    refetch: boolean;
  };
  'cache:set': {
    key: string;
    value: string;
    styles?: Record<string, string[]>;
    optimistic: boolean;
  };
  'cache:rollback': {
    key: string;
    reason: string;
  };
}

/**
 * CacheManager - Unified cache API inspired by TanStack Query
 *
 * @example
 * ```typescript
 * // Invalidate by namespace
 * await i18n.cache.invalidate({ namespace: 'app' });
 *
 * // Optimistic update
 * const previous = i18n.cache.get('en:app:welcome');
 * i18n.cache.set('en:app:welcome', 'New value');
 * // ... if error, rollback:
 * i18n.cache.set('en:app:welcome', previous.value);
 * ```
 */
export class CacheManager {
  private handlerChain: HandlerChain;
  private bus: EventEmitter;
  private currentLocale: () => string;
  private loadNamespaces: (locale: string, namespaces: string[]) => Promise<void>;

  constructor(
    handlerChain: HandlerChain,
    bus: EventEmitter,
    getCurrentLocale: () => string,
    loadNamespaces: (locale: string, namespaces: string[]) => Promise<void>
  ) {
    this.handlerChain = handlerChain;
    this.bus = bus;
    this.currentLocale = getCurrentLocale;
    this.loadNamespaces = loadNamespaces;
  }

  /**
   * Get a cached translation entry
   *
   * @param key - Full cache key (e.g., 'en:app:welcome')
   * @returns Cache entry with value and styles, or null if not found
   */
  get(key: string): CacheEntry | null {
    const entries = this.handlerChain.getEntries();

    // Search through all handler entries
    for (const handlerName in entries) {
      const handlerEntries = entries[handlerName];
      if (handlerEntries && typeof handlerEntries === 'object') {
        if (key in handlerEntries) {
          const rawValue = handlerEntries[key];

          // Parse ContentUnit JSON (stored as { text, styles })
          try {
            if (typeof rawValue === 'string') {
              const parsed = JSON.parse(rawValue);
              if (typeof parsed === 'object' && 'text' in parsed) {
                return {
                  value: String(parsed.text),
                  styles: parsed.styles || undefined,
                };
              }
              // Legacy plain string
              return { value: rawValue, styles: undefined };
            }
            // Already an object
            return {
              value: JSON.stringify(rawValue),
              styles: undefined,
            };
          } catch {
            // If parse fails, treat as plain string
            return {
              value: typeof rawValue === 'string' ? rawValue : JSON.stringify(rawValue),
              styles: undefined,
            };
          }
        }
      }
    }

    return null;
  }

  /**
   * Set a cache entry (for optimistic updates)
   *
   * @param key - Full cache key (e.g., 'en:app:welcome')
   * @param value - Translation value
   * @param styles - Optional styles for indexed tags
   */
  set(key: string, value: string, styles?: Record<string, string[]>): void {
    // Parse the key to get locale, namespace, and translation key
    const [locale, namespace, ...keyParts] = key.split(':');
    const translationKey = keyParts.join(':');

    if (!locale || !namespace || !translationKey) {
      throw new Error(
        `Invalid cache key format: "${key}". Expected format: "locale:namespace:key"`
      );
    }

    // Use the handler chain's setCache method for optimistic updates
    this.handlerChain.setCache(key, value, styles);

    // Emit cache:set event
    this.bus.emit('cache:set', {
      payload: {
        key,
        value,
        styles,
        optimistic: true,
      },
    });
  }

  /**
   * Check if a key exists in cache
   *
   * @param key - Full cache key (e.g., 'en:app:welcome')
   * @returns true if key is cached
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Invalidate cache entries and optionally refetch
   *
   * Like TanStack Query's invalidateQueries, this clears matching cache entries
   * and triggers background refetch by default.
   *
   * @param options - Filter options for invalidation
   *
   * @example
   * ```typescript
   * // Invalidate all
   * await cache.invalidate();
   *
   * // Invalidate by namespace
   * await cache.invalidate({ namespace: 'app' });
   *
   * // Invalidate by locale
   * await cache.invalidate({ locale: 'es' });
   *
   * // Invalidate specific key
   * await cache.invalidate({ key: 'en:app:welcome' });
   *
   * // Invalidate with predicate
   * await cache.invalidate({ predicate: (k) => k.includes('common:') });
   *
   * // Invalidate without refetch
   * await cache.invalidate({ namespace: 'app', refetch: false });
   * ```
   */
  async invalidate(options: InvalidateOptions = {}): Promise<void> {
    const { namespace, locale, key, predicate, refetch = true } = options;
    const currentLocale = this.currentLocale();

    let keysCleared = 0;

    // Determine what to clear
    if (key) {
      // Clear specific key
      const [keyLocale, keyNamespace] = key.split(':');
      if (keyLocale && keyNamespace) {
        this.handlerChain.clearNamespace(keyLocale, keyNamespace);
        keysCleared = 1;
      }
    } else if (namespace && locale) {
      // Clear specific locale + namespace
      this.handlerChain.clearNamespace(locale, namespace);
      keysCleared = this.countClearedKeys(locale, namespace);
    } else if (namespace) {
      // Clear namespace for current locale
      this.handlerChain.clearNamespace(currentLocale, namespace);
      keysCleared = this.countClearedKeys(currentLocale, namespace);
    } else if (locale) {
      // Clear all namespaces for a specific locale
      const entries = this.handlerChain.getEntries();
      const namespacesToClear = new Set<string>();

      for (const handlerName in entries) {
        const handlerEntries = entries[handlerName];
        if (handlerEntries && typeof handlerEntries === 'object') {
          for (const entryKey of Object.keys(handlerEntries)) {
            if (entryKey.startsWith(`${locale}:`) || entryKey.startsWith(`@${locale}:`)) {
              const parts = entryKey.replace('@', '').split(':');
              if (parts[1]) {
                namespacesToClear.add(parts[1]);
              }
            }
          }
        }
      }

      for (const ns of namespacesToClear) {
        this.handlerChain.clearNamespace(locale, ns);
        keysCleared++;
      }
    } else if (predicate) {
      // Clear with custom predicate
      const entries = this.handlerChain.getEntries();
      const toClear = new Map<string, Set<string>>(); // locale -> namespaces

      for (const handlerName in entries) {
        const handlerEntries = entries[handlerName];
        if (handlerEntries && typeof handlerEntries === 'object') {
          for (const entryKey of Object.keys(handlerEntries)) {
            if (predicate(entryKey)) {
              const parts = entryKey.replace('@', '').split(':');
              const entryLocale = parts[0];
              const entryNs = parts[1];
              if (entryLocale && entryNs) {
                if (!toClear.has(entryLocale)) {
                  toClear.set(entryLocale, new Set());
                }
                toClear.get(entryLocale)!.add(entryNs);
                keysCleared++;
              }
            }
          }
        }
      }

      for (const [loc, namespaces] of toClear) {
        for (const ns of namespaces) {
          this.handlerChain.clearNamespace(loc, ns);
        }
      }
    } else {
      // Clear all
      this.handlerChain.clearCache();
      keysCleared = -1; // Indicates all cleared
    }

    // Emit invalidate event
    this.bus.emit('cache:invalidate', {
      payload: {
        filter: options,
        keysCleared,
        refetch,
      },
    });

    // Refetch if requested
    if (refetch) {
      await this.refetchAfterInvalidate(options);
    }
  }

  /**
   * Count keys that would be cleared (for event reporting)
   */
  private countClearedKeys(locale: string, namespace: string): number {
    const entries = this.handlerChain.getEntries();
    let count = 0;
    const prefix = `${locale}:${namespace}:`;
    const namespaceKey = `@${locale}:${namespace}`;

    for (const handlerName in entries) {
      const handlerEntries = entries[handlerName];
      if (handlerEntries && typeof handlerEntries === 'object') {
        for (const key of Object.keys(handlerEntries)) {
          if (key.startsWith(prefix) || key === namespaceKey) {
            count++;
          }
        }
      }
    }

    return count;
  }

  /**
   * Refetch data after invalidation
   */
  private async refetchAfterInvalidate(options: InvalidateOptions): Promise<void> {
    const { namespace, locale, key } = options;
    const currentLocale = this.currentLocale();

    if (key) {
      // Refetch specific namespace from key
      const [keyLocale, keyNamespace] = key.split(':');
      if (keyLocale && keyNamespace) {
        await this.loadNamespaces(keyLocale, [keyNamespace]);
      }
    } else if (namespace) {
      // Refetch namespace for specific or current locale
      await this.loadNamespaces(locale || currentLocale, [namespace]);
    } else if (locale) {
      // For locale-wide invalidation, we can't easily know which namespaces to reload
      // The app will lazy-load them as needed
    }
    // For full cache clear, lazy loading will handle refetch
  }
}
